import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import DateAdapter from '@mui/lab/AdapterMoment';
import DatePicker from '@mui/lab/DatePicker';
import { Button, Chip, FormControlLabel, MenuItem, Radio, RadioGroup, Select, TextField } from '@mui/material';
import { DataGrid, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarDensitySelector, GridToolbarExport, GridToolbarFilterButton, useGridApiContext } from '@mui/x-data-grid';
import { CmmContainer, SalesTable, SalesTableBody, SalesTableCell, SalesTableRow } from 'components/Dashboard';
import 'moment/locale/ko';
import moment from 'moment';
import OrderIncome from './OrderIncome';

const ChipRenderer = (props) => {
  const { value } = props;

  const chipVariant = value ? 'filled' : 'outlined';
  const chipColor = value ? 'success' : 'error';

  return <Chip size="small" label={value ? '완료' : '미완료'} color={chipColor} variant={chipVariant} sx={{ width: '100%' }} />;
};

const SelectEditInput = (props) => {
  const { id, value, field } = props;
  const apiRef = useGridApiContext();

  const handleChange = async (event) => {
    await apiRef.current.setEditCellValue({ id, field, value: event.target.value });
    apiRef.current.updateRows([{ id, [field]: event.target.value }]);
  };

  useEffect(() => {
    apiRef.current.updateRows([{ id, [field]: value }]);
  }, []);

  return (
    <Select value={value || false} onChange={handleChange} fullWidth>
      <MenuItem value={true}>완료</MenuItem>
      <MenuItem value={false}>미완료</MenuItem>
    </Select>
  );
};
const renderSelectEditInput = (params) => {
  return <SelectEditInput {...params} />;
};

const columns = [
  { field: 'id', headerName: 'ID', width: 90, hide: true },
  { field: 'rowNumber', headerName: 'No', width: 90 },
  {
    field: 'orderNo',
    headerName: '주문번호',
    headerAlign: 'center',
    width: 120
  },
  {
    field: 'isIncome',
    headerName: '수입계산서 여부',
    headerAlign: 'center',
    align: 'center',
    width: 130,
    editable: true,
    renderCell: (params) => <ChipRenderer value={params.value} />,
    renderEditCell: renderSelectEditInput
  },
  {
    field: 'depositAt',
    headerName: '입금일',
    headerAlign: 'center',
    align: 'center',
    sortable: false,
    width: 160
  },
  // {
  //   field: 'totalAmount',
  //   headerName: '판매금액',
  //   description: '할인 전 사용권 금액',
  //   type: 'number',
  //   width: 150,
  //   valueGetter: (params) => Number(params.value)
  // },
  // {
  //   field: 'discountAmount',
  //   headerName: '할인금액',
  //   description: '구매량에 따른 할인금액',
  //   type: 'number',
  //   width: 120,
  //   valueGetter: (params) => Number(params.value)
  // },
  {
    field: 'amount',
    headerName: '총 금액',
    headerAlign: 'center',
    description: '총 금액 = 판매 금액 - 할인 금액',
    type: 'number',
    width: 150,
    valueGetter: (params) => Number(params.value)
  },
  {
    field: 'vat',
    headerName: 'VAT',
    headerAlign: 'center',
    type: 'number',
    width: 120,
    valueGetter: (params) => Number(params.row.amount) * 0.1
  },
  {
    field: 'depositAmount',
    headerName: '최종 금액',
    headerAlign: 'center',
    description: '최종결제금액 = 총 금액 + VAT',
    type: 'number',
    width: 150,
    valueGetter: (params) => Number(params.value)
  },
  {
    field: 'createdName',
    headerName: '구매자',
    headerAlign: 'center',
    sortable: false,
    width: 120
  },
  {
    field: 'acCompanyName',
    headerName: '구매업체',
    headerAlign: 'center',
    sortable: false,
    width: 150
  }
];

const color = `rgba(22, 177, 255, 0.12)`;

export default function OrderHistory() {
  const location = useLocation();

  const date = new Date();

  const [editingRowId, setEditingRowId] = useState(null);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [datas, setDatas] = useState({});
  const [rows, setRows] = useState([]);
  const [srchData, setSrchData] = useState({
    startYmd: location?.state?.startYmd || moment(date.getTime()).add('-1', 'y'),
    endYmd: location?.state?.endYmd || moment()
  });
  const [open, setOpen] = useState({ type: 'regist', open: false });

  const handleClickOpen = (e) => {
    setOpen({ type: e.target.id, open: true });
  };

  const handleClose = (value) => {
    if (value) {
      getDataList();
    }
    setOpen({ open: false });
  };

  const updateDatasInRealTime = async (params) => {
    const { id, isIncome } = params;

    await axios
      .patch(`/api/dashboard/order-hist/${id}`, { isIncome })
      .then((response) => {
        getDataList();
        alert(`${params.orderNo} ${response.data.message}`);
      })
      .catch((error) => {
        alert(error.response.data.message);
      });
  };

  const handleRowDoubleClick = (params) => {
    setEditingRowId(params.id);
  };

  const handleRowEditStop = (params) => {
    const row = rows.find((t) => t.id === params.id);
    if (row.isIncome !== params.row.isIncome) {
      updateDatasInRealTime(params.row);
    }
    setEditingRowId(null);
  };

  const handleChangeSrch = (e) => {
    let id = e.target.id.replace('Srch', '');
    setSrchData({ ...srchData, [id]: e.target.value });
  };

  const getDataList = async (e) => {
    const { startYmd, endYmd, username } = srchData;
    if (!!!startYmd) {
      alert('발급 시작일을 입력해 주세요.');
      return;
    }
    if (!!!endYmd) {
      alert('발급 종료일을 입력해 주세요.');
      return;
    }

    let params = {
      startYmd: moment(startYmd).format('YYYY-MM-DD'),
      endYmd: moment(endYmd).format('YYYY-MM-DD')
    };

    const response = await axios.get(`/api/dashboard/order-hist`, { params });
    if (response.data) {
      const result = response.data;
      setDatas({ orderCnt: result?.orderCnt, couponCnt: result?.couponCnt, amount: result?.amount, vat: result?.vat, totalSales: result?.totalSales });
      setRows(result.rows);
    }
  };

  useEffect(() => {
    getDataList();
  }, []);

  return (
    <Box sx={{ px: 2 }}>
      <Stack spacing={2}>
        <CmmContainer title="주문 내역" sx={{ width: '100%', display: 'block' }}>
          <Stack direction="row" spacing={2}>
            <RadioGroup row defaultValue="order" aria-labelledby="demo-row-radio-buttons-group-label" name="row-radio-buttons-group">
              <FormControlLabel value="order" control={<Radio />} label="주문일자" />
              <FormControlLabel value="disabled" disabled control={<Radio />} label="입금일자" />
            </RadioGroup>
            <LocalizationProvider dateAdapter={DateAdapter}>
              <DatePicker
                inputFormat={'yyyy-MM-DD'}
                label="시작일"
                value={srchData.startYmd}
                onChange={(newValue) => {
                  setSrchData({ ...srchData, startYmd: newValue });
                }}
                renderInput={(params) => <TextField {...params} helperText={null} />}
                disableMaskedInput={true}
              />
              <DatePicker
                inputFormat={'yyyy-MM-DD'}
                label="종료일"
                value={srchData.endYmd}
                onChange={(newValue) => {
                  setSrchData({ ...srchData, endYmd: newValue });
                }}
                renderInput={(params) => <TextField {...params} helperText={null} />}
                disableMaskedInput={true}
              />
            </LocalizationProvider>
            {/* <TextField id="usernameSrch" variant="outlined" label="담당자명" value={srchData?.pgmNm || ''} onChange={handleChangeSrch} /> */}
            <Button variant="contained" onClick={getDataList}>
              검색
            </Button>
          </Stack>
        </CmmContainer>
        <SalesTable>
          <SalesTableBody>
            <SalesTableRow>
              <SalesTableCell th sx={{ width: '10%' }}>
                주문 건
              </SalesTableCell>
              <SalesTableCell unit="건" sx={{ textAlign: 'right', width: '23%' }}>
                {datas?.orderCnt || 0}
              </SalesTableCell>
              <SalesTableCell th sx={{ width: '10%' }}>
                사용권 구매 수량
              </SalesTableCell>
              <SalesTableCell unit="개" sx={{ textAlign: 'right', width: '23%' }}>
                {datas?.couponCnt || 0}
              </SalesTableCell>
              <SalesTableCell th sx={{ width: '10%' }}></SalesTableCell>
              <SalesTableCell sx={{ textAlign: 'right', width: '23%' }}></SalesTableCell>
            </SalesTableRow>
            <SalesTableRow>
              <SalesTableCell th sx={{ width: '10%' }}>
                매출액
              </SalesTableCell>
              <SalesTableCell unit="원" sx={{ textAlign: 'right', width: '23%' }}>
                {datas?.amount?.toLocaleString() || 0}
              </SalesTableCell>
              <SalesTableCell th sx={{ width: '10%' }}>
                세금
              </SalesTableCell>
              <SalesTableCell unit="원" sx={{ textAlign: 'right', width: '23%' }}>
                {datas?.vat?.toLocaleString() || 0}
              </SalesTableCell>
              <SalesTableCell th sx={{ width: '10%' }}>
                총 결제액
              </SalesTableCell>
              <SalesTableCell unit="원" sx={{ textAlign: 'right', width: '23%' }}>
                {datas?.totalSales?.toLocaleString() || 0}
              </SalesTableCell>
            </SalesTableRow>
          </SalesTableBody>
        </SalesTable>
        <CmmContainer
          title="주문 목록"
          sx={{ width: '100%', height: 450 }}
          component={
            <Stack spacing={1} direction="row">
              <Button id="regist" variant="contained" onClick={handleClickOpen}>
                수입계산서 작성
              </Button>
              <Button id="detail" variant="contained" color="secondary" onClick={handleClickOpen}>
                수입계산서 상세보기
              </Button>
            </Stack>
          }
        >
          <DataGrid
            rows={rows}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 10
                }
              }
            }}
            pageSizeOptions={[10]}
            // checkboxSelection
            disableRowSelectionOnClick
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={(newRowSelectionModel) => {
              setRowSelectionModel(newRowSelectionModel);
            }}
            slots={{
              toolbar: CustomToolbar
            }}
            slotProps={{ toolbar: { rowSelectionModel, setRowSelectionModel, getDataList } }}
            editMode="row"
            onRowDoubleClick={handleRowDoubleClick}
            onRowEditStop={handleRowEditStop}
            isRowEditable={(params) => params.id === editingRowId}
          />
        </CmmContainer>
      </Stack>
      {open.open && <OrderIncome open={open.open} openType={open.type} onClose={handleClose} srchParams={srchData} />}
    </Box>
  );
}

function CustomToolbar(props) {
  const { rowSelectionModel, setRowSelectionModel, getDataList } = props;
  const apiRef = useGridApiContext(); //datagrid 하위에서만 선언 및 사용 가능.

  const handleClickDelete = async (e) => {
    await axios
      .delete(`/api/dashboard/order-hist`, { isIncome: rowSelectionModel })
      .then((response) => {
        getDataList();
        setRowSelectionModel([]);
        alert(response.data.message);
      })
      .catch((error) => {
        alert(error.response.data.message);
      });
    // apiRef.setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } })
    // console.log(apiRef.current.getCellMode());
  };

  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <GridToolbarExport />
      <Box sx={{ flex: 'auto 1 1' }}></Box>
      <Stack spacing={1}>
        {/* <Button variant="contained" onClick={handleClickDelete}>
          삭제
        </Button> */}
      </Stack>
    </GridToolbarContainer>
  );
}
